/* ==========================================================================
   plan-road.js — the road. One drawing, used at four magnifications.

   A layaway plan is a line of paydays that ends at a door. Every surface that
   talks about a plan draws the same line, so the shape a shopper plays with on
   the homepage is the shape they operate in their account after they sign.

   The grammar, and it is load-bearing:

     tick        one scheduled payment
     orange dot  the first payment — the only moment money moves at the start
     hollow ring a skipped payment (a break), so a break reads as a gap, never
                 as a payment that disappeared
     flag        payoff. The plan ENDS here; nothing after it is a payment
     dashed arm  the ship window. It is our obligation to DISPATCH, so it is
                 drawn as a bracket, never as a tick — a tick would read as a
                 dated event we do not control
     door        the item arriving. No date is ever printed against it

   The line is redrawn to the element's real pixel width so stroke weights and
   the door stay constant while only the road stretches. Above 32 payments the
   ticks would collide, so the count is drawn as a caption instead of a lie.
   ========================================================================== */
(function () {
  'use strict';

  if (window.LaybyRoad) return;

  var MAX_TICKS = 32;
  var H = 64;          // the drawing's own height, in its own units
  var Y = 30;          // the line
  var PAD_START = 14;
  var TAIL = 84;       // room after payoff for the bracket and the door

  function el(name, attrs) {
    var n = document.createElementNS('http://www.w3.org/2000/svg', name);
    for (var k in attrs) if (attrs.hasOwnProperty(k)) n.setAttribute(k, attrs[k]);
    return n;
  }

  /**
   * @param {SVGElement} svg    an <svg data-road> element
   * @param {object} opts
   *   payments  {number} scheduled payments. 0 or undefined draws nothing.
   *   skipAt    {number} index of a skipped payment, or null
   *   paid      {number} how many payments have happened, when the road is
   *                      telling a story in progress. Paid ticks take the
   *                      accent — orange is where money has moved — and with
   *                      paid = 0 the first dot is drawn hollow, because
   *                      nothing has moved yet. Leave undefined for a plan
   *                      that has not started.
   *   flagLit   {boolean} payoff reached: the flag takes the accent
   *   doorLit   {boolean} the item is on its way: the door takes the accent
   *   ink       {string} line colour  (default currentColor)
   *   accent    {string} first-payment dot colour
   *   muted     {string} the carrier bracket
   */
  function draw(svg, opts) {
    if (!svg) return;
    var o = opts || {};
    var n = Math.max(0, parseInt(o.payments, 10) || 0);

    while (svg.firstChild) svg.removeChild(svg.firstChild);
    if (!n) return;                       // no plan → an empty rule, not a fake one

    var W = Math.max(240, Math.round(svg.clientWidth || svg.parentNode.clientWidth || 320));
    svg.setAttribute('viewBox', '0 0 ' + W + ' ' + H);
    svg.setAttribute('preserveAspectRatio', 'xMinYMid meet');

    var ink = o.ink || 'currentColor';
    var accent = o.accent || '#F26A21';
    var muted = o.muted || '#6A625A';

    var x0 = PAD_START;
    var x1 = W - TAIL;
    if (x1 - x0 < 60) x1 = x0 + 60;
    var span = x1 - x0;
    var ticks = Math.min(n, MAX_TICKS);

    svg.appendChild(el('line', {
      x1: x0, y1: Y, x2: x1, y2: Y, stroke: ink, 'stroke-width': 2.2, 'stroke-linecap': 'round'
    }));

    // quarter marks — the same marks the account's road card uses
    [0.25, 0.5, 0.75].forEach(function (f) {
      var x = x0 + span * f;
      svg.appendChild(el('line', { x1: x, y1: Y - 14, x2: x, y2: Y - 11, stroke: ink, 'stroke-width': 1.5, opacity: 0.55 }));
    });

    var paid = o.paid == null ? null : Math.max(0, parseInt(o.paid, 10) || 0);

    for (var i = 0; i < ticks; i++) {
      var x = ticks === 1 ? x0 : x0 + span * (i / (ticks - 1));
      var isPaid = paid != null && i < paid;
      if (o.skipAt != null && i === o.skipAt) {
        svg.appendChild(el('circle', { cx: x, cy: Y, r: 4.5, fill: '#fff', stroke: ink, 'stroke-width': 2 }));
      } else if (i === 0) {
        if (paid === 0) {
          svg.appendChild(el('circle', { cx: x, cy: Y, r: 5, fill: '#fff', stroke: accent, 'stroke-width': 2.2 }));
        } else {
          svg.appendChild(el('circle', { cx: x, cy: Y, r: 5, fill: accent }));
        }
      } else {
        var h = i === ticks - 1 ? 9 : 5;
        svg.appendChild(el('line', {
          x1: x, y1: Y - h, x2: x, y2: Y + h,
          stroke: isPaid ? accent : ink, 'stroke-width': isPaid ? 2.6 : 2, 'stroke-linecap': 'round'
        }));
      }
    }

    if (n > MAX_TICKS) {
      var cap = el('text', {
        x: x0 + span / 2, y: Y + 24, 'text-anchor': 'middle', fill: muted,
        'font-size': 11, 'font-family': 'inherit', 'font-weight': 600
      });
      cap.textContent = n + ' payments';
      svg.appendChild(cap);
    }

    // payoff
    var flagColor = o.flagLit ? accent : ink;
    svg.appendChild(el('path', { d: 'M' + x1 + ' ' + (Y - 24) + ' v24', stroke: flagColor, 'stroke-width': 2, 'stroke-linecap': 'round' }));
    svg.appendChild(el('path', { d: 'M' + (x1 + 1) + ' ' + (Y - 24) + ' h13 l-4 5 4 5 h-13z', fill: flagColor }));

    // the carrier's allowance, which is not ours to date
    svg.appendChild(el('line', {
      x1: x1 + 16, y1: Y, x2: x1 + 52, y2: Y, stroke: muted, 'stroke-width': 2,
      'stroke-dasharray': '3 4', 'stroke-linecap': 'round'
    }));

    var doorColor = o.doorLit ? accent : ink;
    svg.appendChild(el('rect', { x: x1 + 54, y: Y - 13, width: 12, height: 26, rx: 1.5, fill: 'none', stroke: doorColor, 'stroke-width': 2 }));
    svg.appendChild(el('circle', { cx: x1 + 63, cy: Y + 1, r: 1.4, fill: doorColor }));
  }

  /* --------------------------------------------------------------------------
     Draw at the width the element ACTUALLY ends up.

     draw() measured clientWidth once, at register time. On a cold load that is
     often before the section's stylesheet has applied and before the webfont
     has swapped, so it read a narrower box than the final one — and because
     the SVG is preserveAspectRatio="xMinYMid meet" against a fixed height, a
     viewBox narrower than the box does not stretch to fill it: the drawing
     sits at natural size against the left edge with dead space to its right.
     That is the "compacts up on first load, correct on refresh" bug, refresh
     being the pass where the font is already cached.

     A ResizeObserver fires once on observe and again on every later change,
     which covers the stylesheet, the font swap and the window together. Redraw
     only when the width really moved: draw() rewrites the viewBox, not the
     element's box, so this cannot feed itself, and the guard keeps it cheap.
     -------------------------------------------------------------------------- */
  var registry = [];

  function widthOf(svg) {
    return Math.round(svg.clientWidth || (svg.parentNode && svg.parentNode.clientWidth) || 0);
  }

  function register(svg, getOpts) {
    var entry = { svg: svg, get: getOpts, w: -1 };
    registry.push(entry);
    draw(svg, getOpts());
    entry.w = widthOf(svg);

    if (typeof ResizeObserver === 'function') {
      new ResizeObserver(function () {
        var w = widthOf(svg);
        if (w === entry.w) return;
        entry.w = w;
        draw(svg, entry.get());
      }).observe(svg);
    }
  }

  function redrawAll() {
    registry.forEach(function (r) {
      draw(r.svg, r.get());
      r.w = widthOf(r.svg);
    });
  }

  var t;
  window.addEventListener('resize', function () {
    clearTimeout(t);
    t = setTimeout(redrawAll, 120);
  });

  // The caption is set in the page's own font; a swap changes its metrics, and
  // on browsers with no ResizeObserver this is the only second chance.
  if (document.fonts && document.fonts.ready && document.fonts.ready.then) {
    document.fonts.ready.then(redrawAll).catch(function () {});
  }

  window.LaybyRoad = { draw: draw, register: register, redrawAll: redrawAll };
})();
